import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import moment from 'moment';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import SendIcon from '@material-ui/icons/Send';

import { appUrl } from '../../../../../../utils/config';

const useStyles = makeStyles(() => ({
  paper: { borderRadius: 12, width: 500, maxWidth: '95vw' },
  titleBar: {
    backgroundColor: '#2196f3',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: '12px 12px 0 0',
  },
  titleText: { color: '#fff', fontWeight: 600, fontSize: '1rem' },
  closeBtn: { color: '#fff', padding: 4 },
  chatArea: {
    backgroundColor: '#dce8f0',
    minHeight: 280,
    maxHeight: 340,
    overflowY: 'auto',
    padding: '12px 10px',
  },
  bubble: {
    padding: '8px 12px',
    borderRadius: 15,
    maxWidth: '78%',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: 12,
  },
  sentBubble: {
    backgroundColor: '#dcf8c6',
    marginLeft: 'auto',
    marginRight: 10,
  },
  receivedBubble: {
    backgroundColor: '#ffffff',
    marginRight: 'auto',
    marginLeft: 10,
  },
  senderName: { fontWeight: 'bold', fontSize: '0.82rem' },
  timestamp: { color: '#929292', fontSize: '0.72rem' },
  messageText: { fontSize: '0.85rem', marginTop: 2 },
  fileLink: {
    display: 'flex',
    alignItems: 'center',
    color: 'blue',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontSize: '0.82rem',
    marginTop: 4,
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #000',
    borderRadius: 4,
    padding: '4px 8px',
    backgroundColor: '#fff',
  },
  filePreviewBar: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f1f1f1',
    borderRadius: 4,
    padding: '4px 8px',
    marginBottom: 6,
  },
}));

const getApiBase = () =>
  `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;

// viewType: 'pre-schedule' | 'post-schedule' | 'post-delivery'
const CommentsDialog = ({ open, onClose, shipment, viewType }) => {
  const classes = useStyles();
  const isPreSchedule = viewType === 'preSchedule';

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const chatRef = useRef(null);
  const tokenData = jwtDecode(localStorage.getItem('authToken') || '{}');

  const scrollToBottom = () => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  };

  // ── Pre-Schedule format ──────────────────────────────────────────────────
  const formatPreScheduleMessages = (chatData) =>
    chatData.map((chat) => ({
      rawTime:         chat.chatTimestamp,
      sender:          chat.chatSenderName,
      time:            moment(chat.chatTimestamp).format('DD-MMM-YYYY HH:mm').toUpperCase(),
      text:            chat.chatData,
      type:            chat.chatSenderEmail === tokenData.email_address ? 'sent' : 'received',
      file:            chat.chatFileName || null,
      schrockenChatID: chat.schrockenChatID || null,
    }));

  // ── Post-Schedule / Post-Delivery format ─────────────────────────────────
  const formatShipmentMessages = (list) =>
    list.map((chat) => ({
      rawTime: chat.chatTimestamp,
      sender:  chat.chatSenderName,
      time:    moment(chat.chatTimestamp).format('DD-MMM-YYYY HH:mm').toUpperCase(),
      text:    chat.chatData,
      type:    chat.chatSenderEmail === tokenData.email_address ? 'sent' : 'received',
    }));

  // ── Fetch ─────────────────────────────────────────────────────────────────
  // silent=true skips the loading spinner (used when refreshing after send)
  const fetchMessages = (silent = false) => {
    if (!silent) setLoading(true);
    setError('');

    if (isPreSchedule) {
      return axios
        .post(`${getApiBase()}/fetchPOBatchItemChatData`, {
          schrockenBatchID: shipment?.schrockenDeliveryScheduleBatchID,
        })
        .then((res) => {
          const list = res.data?.chatData;
          if (Array.isArray(list)) setMessages(formatPreScheduleMessages(list));
          setTimeout(scrollToBottom, 100);
        })
        .catch(() => setError('Failed to load comments. Please try again.'))
        .finally(() => { if (!silent) setLoading(false); });
    }

    // Post-schedule / post-delivery: call both shipment and batch chat APIs concurrently
    const shipmentPromise = axios.get(
      `${getApiBase()}/fetchShipmentChatData/${shipment?.collabrixFGBatchShipmentID}`
    );

    const batchPromise = shipment?.schrockenDeliveryScheduleBatchID
      ? axios.post(`${getApiBase()}/fetchPOBatchItemChatData`, {
          schrockenBatchID: shipment.schrockenDeliveryScheduleBatchID,
        })
      : Promise.resolve(null);

    return Promise.all([shipmentPromise, batchPromise])
      .then(([shipmentRes, batchRes]) => {
        const shipmentList = shipmentRes.data?.data?.shipmentChatResult ?? [];
        const batchList    = batchRes?.data?.chatData ?? [];

        const combined = [
          ...formatShipmentMessages(Array.isArray(shipmentList) ? shipmentList : []),
          ...formatPreScheduleMessages(Array.isArray(batchList) ? batchList : []),
        ].sort((a, b) => new Date(a.rawTime) - new Date(b.rawTime));

        setMessages(combined);
        setTimeout(scrollToBottom, 100);
      })
      .catch(() => setError('Failed to load comments. Please try again.'))
      .finally(() => { if (!silent) setLoading(false); });
  };

  const shipmentID = isPreSchedule
    ? shipment?.schrockenDeliveryScheduleBatchID
    : shipment?.collabrixFGBatchShipmentID;

  useEffect(() => {
    if (!open || !shipmentID) return;
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shipmentID]);

  // ── Send ──────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!newMessage.trim() && !file) return;
    setSending(true);
    const text = newMessage.trim();
    const fileToSend = file;  // capture before clearing state
    setNewMessage('');
    setFile(null);

    try {
      if (isPreSchedule) {
        const formData = new FormData();
        formData.append('vendorBatchNumber',  shipment?.vendorBatchNumber  || '');
        formData.append('vendorMosymphonyID', shipment?.vendorMosymphonyID || '');
        formData.append('schrockenBatchID',   shipment?.schrockenDeliveryScheduleBatchID || '');
        formData.append('chatDataObject', JSON.stringify({
          chatSenderName:  tokenData.full_name,
          chatSenderEmail: tokenData.email_address,
          chatTimestamp:   new Date().toISOString(),
          chatData:        text,
          chatFileHash:    fileToSend ? 'notempty' : '',
          chatFileName:    fileToSend ? fileToSend.name : '',
        }));
        formData.append('createdBy',     tokenData.full_name);
        formData.append('lastUpdatedBy', tokenData.full_name);
        if (fileToSend) formData.append('files', fileToSend);
        await axios.post(`${getApiBase()}/addPOBatchItemChatData`, formData);
      } else {
        await axios.post(`${getApiBase()}/addShipmentChatData`, {
          collabrixFGBatchShipmentID: shipment?.collabrixFGBatchShipmentID || '',
          vendorMosymphonyID:         shipment?.vendorMosymphonyID         || '',
          brandOwnerMosymphonyID:     shipment?.brandOwnerMosymphonyID     || '',
          chatSenderName:             tokenData.full_name,
          chatSenderEmail:            tokenData.email_address,
          chatTimestamp:              new Date().toISOString(),
          chatData:                   text
        });
      }

      // refresh chat silently after successful send
      await fetchMessages(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleDownload = (schrockenChatID) => {
    axios
      .post(`${getApiBase()}/downloadChatFile`, { docType: 'chat', schrockenChatID })
      .then((res) => {
        if (res.data.status) {
          const bytes = new Uint8Array(res.data.fileData.data);
          const a = document.createElement('a');
          a.href = URL.createObjectURL(new Blob([bytes], { type: 'application/octet-stream' }));
          a.download = res.data.fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      })
      .catch(console.error);
  };

  const handleClose = () => {
    setMessages([]);
    setNewMessage('');
    setFile(null);
    setError('');
    onClose && onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} classes={{ paper: classes.paper }}>
      {/* Header */}
      <DialogTitle disableTypography style={{ padding: 0 }}>
        <div className={classes.titleBar}>
          <Typography className={classes.titleText}>Comment Section</Typography>
          <IconButton size="small" className={classes.closeBtn} onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent style={{ padding: 0 }}>
        {/* Messages area */}
        <div className={classes.chatArea} ref={chatRef}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress color="primary" thickness={7} />
            </Box>
          ) : error ? (
            <Box
              style={{
                border: '2px solid rgb(255,0,0)',
                backgroundColor: 'rgb(255,240,240)',
                textAlign: 'center',
                padding: '0.8rem',
                margin: '1rem',
                borderRadius: 4,
              }}
            >
              <Typography color="error" variant="body2">{error}</Typography>
            </Box>
          ) : messages.length === 0 ? (
            <Typography
              variant="body2"
              style={{ color: '#9E9E9E', textAlign: 'center', padding: '3rem 1rem' }}
            >
              No comments yet. Start the conversation.
            </Typography>
          ) : (
            messages.map((msg, idx) => (
              <Box
                key={idx}
                display="flex"
                flexDirection="column"
                alignItems={msg.type === 'sent' ? 'flex-end' : 'flex-start'}
              >
                <div
                  className={`${classes.bubble} ${
                    msg.type === 'sent' ? classes.sentBubble : classes.receivedBubble
                  }`}
                >
                  <Typography
                    className={classes.senderName}
                    style={{ color: msg.type === 'sent' ? '#ef7b52' : 'green' }}
                  >
                    {msg.sender}
                  </Typography>
                  <Typography className={classes.timestamp}>{msg.time}</Typography>
                  <Typography className={classes.messageText}>{msg.text}</Typography>
                  {msg.file && (
                    <div
                      className={classes.fileLink}
                      onClick={() => handleDownload(msg.schrockenChatID)}
                    >
                      <GetAppIcon fontSize="small" style={{ marginRight: 4 }} />
                      {msg.file}
                    </div>
                  )}
                </div>
              </Box>
            ))
          )}
        </div>

        {/* Input area */}
        <Box padding="10px" borderTop="1px solid #ddd">
          {isPreSchedule && file && (
            <div className={classes.filePreviewBar}>
              <Typography variant="body2" style={{ flex: 1, fontSize: '0.82rem' }}>
                {file.name}
              </Typography>
              <IconButton size="small" onClick={() => setFile(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          )}

          <div className={classes.inputRow}>
            {isPreSchedule && (
              <IconButton color="primary" component="label" size="small">
                <AttachFileIcon style={{ color: '#555', fontSize: 20 }} />
                <input
                  type="file"
                  hidden
                  accept=".csv,.xls,.xlsx,.pdf,.jpeg,.png,.jpg,.bmp,.doc,.docx"
                  onChange={(e) => e.target.files[0] && setFile(e.target.files[0])}
                />
              </IconButton>
            )}

            <TextField
              placeholder="Type your comment here..."
              variant="standard"
              InputProps={{ disableUnderline: true }}
              fullWidth
              value={newMessage}
              onChange={(e) => {
                if (e.target.value.length < 256) setNewMessage(e.target.value);
              }}
              onBlur={(e) => {
                const v = e.target.value;
                if (typeof v === 'string' && v.trim() !== v) setNewMessage(v.trim());
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setNewMessage(e.target.value.trim());
                  handleSend();
                }
              }}
            />

            {sending ? (
              <IconButton size="small" style={{ marginLeft: 4 }}>
                <CircularProgress color="primary" size={20} thickness={3} />
              </IconButton>
            ) : (
              <IconButton
                color="primary"
                size="small"
                style={{ marginLeft: 4 }}
                onClick={handleSend}
                disabled={!newMessage.trim() && !file}
              >
                <SendIcon style={{ fontSize: 20 }} />
              </IconButton>
            )}
          </div>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CommentsDialog;
